import { getResolvedApiSuiteBase, getSsoVerifyBase, isChatStandalone } from "./apiBase";
import { isChatHub2Desk } from "./chatHub2";

export type SuiteUserProfile = {
  id: number;
  username: string;
  email: string | null;
  phone: string | null;
  client: { id: number; name: string; logo?: string | null } | null;
};

/** Lit `suite_user_id` / `userId` dans l’URL et aligne sessionStorage + localStorage. */
export function syncSuiteUserIdFromQuery(): string | null {
  if (isChatHub2Desk()) return null;
  if (typeof window === "undefined") return null;
  try {
    const q = new URLSearchParams(window.location.search);
    const v = q.get("suite_user_id") || q.get("userId");
    if (v && /^\d+$/.test(v)) {
      sessionStorage.setItem("suite_user_id", v);
      localStorage.setItem("suite_user_id", v);
      localStorage.setItem("userId", v);
      window.dispatchEvent(new CustomEvent("suiteUserIdReceived", { detail: { userId: v } }));
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function readStoredSuiteUserId(): string | null {
  if (typeof window.readStoredSuiteUserId === "function") {
    const fromShared = window.readStoredSuiteUserId();
    if (fromShared) return String(fromShared);
  }
  try {
    const local = localStorage.getItem("suite_user_id") || localStorage.getItem("userId");
    if (!local || !/^\d+$/.test(local)) {
      sessionStorage.removeItem("suite_user_id");
      return null;
    }
    return local;
  } catch {
    return null;
  }
}

/** Chat dans le hub : même origine que la suite → localStorage partagé. */
export function isChatEmbeddedInHub(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

export function clearStoredSuiteUserIds(): void {
  if (isChatHub2Desk()) return;
  if (typeof window.clearSuiteStoredIdentity === "function") {
    window.clearSuiteStoredIdentity();
    return;
  }
  try {
    sessionStorage.removeItem("suite_user_id");
    localStorage.removeItem("suite_user_id");
    localStorage.removeItem("userId");
  } catch {
    /* ignore */
  }
}

const SUITE_SESSION_ERRORS = new Set(["session_required", "session_or_userid_required", "unauthorized"]);

export function isSuiteSessionErrorCode(error?: string | null): boolean {
  return typeof error === "string" && SUITE_SESSION_ERRORS.has(error);
}

export function resetChatSessionGateEmit(): void {
  if (typeof window.resetSuiteSessionGateEmit === "function") {
    window.resetSuiteSessionGateEmit();
  }
}

const HUB2_FETCH_MS = 8_000;

async function fetchHub2Json<T>(url: string): Promise<{ ok: boolean; status: number; data: T | null }> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), HUB2_FETCH_MS);
  try {
    const res = await fetch(url, { credentials: "include", cache: "no-store", signal: ctrl.signal });
    const data = (await res.json().catch(() => null)) as T | null;
    return { ok: res.ok, status: res.status, data };
  } catch {
    return { ok: false, status: 0, data: null };
  } finally {
    window.clearTimeout(t);
  }
}

/** Une seule vérif cookie à la fois (évite le verrou session PHP whoami×N). */
let hub2ProfileInFlight: Promise<{
  ok: boolean;
  forbidden?: boolean;
  profile?: SuiteUserProfile;
  message?: string;
}> | null = null;

async function fetchHub2ProfileOnce(): Promise<{
  ok: boolean;
  forbidden?: boolean;
  profile?: SuiteUserProfile;
  message?: string;
}> {
  const base = getResolvedApiSuiteBase();
  if (!base) return { ok: false, message: "API suite introuvable." };
  const who = await fetchHub2Json<{ ok?: boolean; user_id?: number; username?: string }>(
    `${base}/hub2-whoami.php`,
  );
  if (!who.ok || !who.data?.ok || !who.data.user_id) {
    return { ok: false, message: "Session du bureau absente." };
  }
  const ping = await fetchHub2Json<{
    success?: boolean;
    userId?: number;
    client?: SuiteUserProfile["client"];
    error?: string;
  }>(`${base}/chat-ping.php`);
  if (!ping.ok || !ping.data?.success || !ping.data.userId) {
    const err = typeof ping.data?.error === "string" ? ping.data.error : "";
    const forbidden =
      ping.status === 403 || err === "forbidden_app_permission" || err === "admin_apps_disabled" || err === "no_active_client";
    return {
      ok: false,
      forbidden,
      message: forbidden ? "Accès Chat refusé." : "Chat n’a pas repris la session du bureau.",
    };
  }
  return {
    ok: true,
    profile: {
      id: ping.data.userId,
      username: String(who.data.username || ""),
      email: null,
      phone: null,
      client: ping.data.client ?? null,
    },
  };
}

export async function fetchHub2Profile(): Promise<{
  ok: boolean;
  forbidden?: boolean;
  profile?: SuiteUserProfile;
  message?: string;
}> {
  if (hub2ProfileInFlight) return hub2ProfileInFlight;
  hub2ProfileInFlight = (async () => {
    // 3 essais : cookie parfois pas encore visible juste après ouverture iframe / purge.
    let last: { ok: boolean; forbidden?: boolean; profile?: SuiteUserProfile; message?: string } = {
      ok: false,
      message: "Session du bureau absente.",
    };
    for (let attempt = 0; attempt < 3; attempt += 1) {
      last = await fetchHub2ProfileOnce();
      if (last.ok || last.forbidden) return last;
      if (attempt < 2) {
        await new Promise((r) => window.setTimeout(r, 400 * (attempt + 1)));
      }
    }
    return last;
  })();
  try {
    return await hub2ProfileInFlight;
  } finally {
    hub2ProfileInFlight = null;
  }
}

export async function announceChatHub2User(): Promise<void> {
  const result = await fetchHub2Profile();
  if (!result.ok || !result.profile) {
    const miss = () => window.dispatchEvent(new CustomEvent("aurelia-chat-desk-missing"));
    miss();
    window.setTimeout(miss, 400);
    window.setTimeout(miss, 1400);
    return;
  }
  const userId = String(result.profile.id);
  const send = () => {
    window.dispatchEvent(new CustomEvent("suiteUserIdReceived", { detail: { userId } }));
  };
  send();
  window.setTimeout(send, 400);
  window.setTimeout(send, 1400);
}

export async function fetchSsoVerify(suiteUserId: string): Promise<{
  ok: boolean;
  forbidden?: boolean;
  profile?: SuiteUserProfile;
  message?: string;
}> {
  if (isChatHub2Desk() || isChatStandalone()) {
    return fetchHub2Profile();
  }
  const base = getSsoVerifyBase();
  if (!base) {
    return { ok: false, message: "API suite introuvable (URL ou VITE_API_SUITE_BASE)." };
  }
  const url = `${base}/sso-verify.php?suite_user_id=${encodeURIComponent(suiteUserId)}&app=${encodeURIComponent("Chat")}`;
  try {
    const ctrl = new AbortController();
    const t = window.setTimeout(() => ctrl.abort(), 15000);
    const res = await fetch(url, { method: "GET", credentials: "include", cache: "no-store", signal: ctrl.signal });
    window.clearTimeout(t);
    let data: Record<string, unknown>;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      return { ok: false, message: `Réponse non JSON (${res.status}). Vérifier l’URL api_suite : ${base}` };
    }
    if (!res.ok || !data.success) {
      const err = typeof data.error === "string" ? data.error : "";
      const forbidden =
        res.status === 403 || err === "forbidden_app_permission" || err === "admin_apps_disabled";
      return {
        ok: false,
        forbidden,
        message: typeof data.message === "string" ? data.message : `HTTP ${res.status}`,
      };
    }
    const u = data.user as Record<string, unknown> | undefined;
    if (!u) {
      return { ok: false, message: "Réponse SSO invalide." };
    }
    const idNum = typeof u.id === "number" ? u.id : parseInt(String(u.id), 10);
    if (!Number.isFinite(idNum) || idNum <= 0) {
      return { ok: false, message: "Réponse SSO invalide." };
    }
    const profile: SuiteUserProfile = {
      id: idNum,
      username: String(u.username ?? ""),
      email: (u.email as string) ?? null,
      phone: (u.phone as string) ?? null,
      client: (u.client as SuiteUserProfile["client"]) ?? null,
    };
    return { ok: true, profile };
  } catch (e) {
    const aborted =
      (e instanceof Error && e.name === "AbortError") ||
      (typeof DOMException !== "undefined" && e instanceof DOMException && e.name === "AbortError");
    if (aborted) {
      return { ok: false, message: "Délai dépassé (15 s) — vérifier l’URL api_suite et le réseau." };
    }
    return { ok: false, message: e instanceof Error ? e.message : "Erreur réseau" };
  }
}
